/**
 * Matrix-style main menu, settings, and controls screens.
 */
export class MainMenu {
  /**
   * @param {{
   *   root: HTMLElement;
   *   settings: import('./UiSettingsController.js').UiSettingsController;
   *   onEnter: () => void;
   *   onSettingsChange: () => void;
   *   onUiInteract?: () => void;
   * }} options
   */
  constructor({ root, settings, onEnter, onSettingsChange, onUiInteract = null }) {
    this._root = root;
    this._settings = settings;
    this._onEnter = onEnter;
    this._onSettingsChange = onSettingsChange;
    this._onUiInteract = onUiInteract;
    /** @type {'main' | 'settings' | 'controls'} */
    this._screen = 'main';
    /** @type {HTMLElement | null} */
    this._panel = null;
    /** Main screen shows RESUME instead of ENTER when returning from gameplay. */
    this._resumeMode = false;
  }

  get isVisible() {
    return !this._root.classList.contains('is-hidden');
  }

  mount() {
    this._root.classList.add('matrix-ui');
    this._root.replaceChildren();
    this._renderScreen();
  }

  /** Show menu overlay (e.g. Esc pause). */
  show({ resume = false } = {}) {
    this._resumeMode = resume;
    this._screen = 'main';
    this._root.classList.remove('is-hidden', 'is-exiting');
    this._renderScreen();
  }

  /** Smooth hide when entering simulation. */
  exitToGame() {
    this._root.classList.add('is-exiting');
    return new Promise((resolve) => {
      const done = () => {
        this._root.classList.add('is-hidden');
        this._root.classList.remove('is-exiting');
        resolve();
      };
      this._root.addEventListener('transitionend', done, { once: true });
      window.setTimeout(done, 650);
    });
  }

  dispose() {
    this._root.replaceChildren();
    this._root.classList.remove('matrix-ui', 'is-hidden', 'is-exiting');
    this._panel = null;
  }

  _renderScreen() {
    this._root.replaceChildren();
    const panel = document.createElement('div');
    panel.className = 'matrix-panel';
    this._panel = panel;

    if (this._screen === 'main') {
      panel.innerHTML = `
        <h1 class="matrix-title">MATRIX CITY</h1>
        <p class="matrix-subtitle">ENTER THE SIMULATION</p>
        <div class="matrix-menu-actions">
          <button type="button" class="matrix-btn" data-action="enter">[ ${this._resumeMode ? 'RESUME' : 'ENTER'} ]</button>
          <button type="button" class="matrix-btn" data-action="settings">[ SETTINGS ]</button>
          <button type="button" class="matrix-btn" data-action="controls">[ CONTROLS ]</button>
        </div>
      `;
    } else if (this._screen === 'controls') {
      panel.innerHTML = `
        <div class="matrix-screen-title">CONTROLS</div>
        <div class="matrix-controls-list">
          <div><span>WASD</span> — Move</div>
          <div><span>MOUSE</span> — Look</div>
          <div><span>SHIFT</span> — Sprint</div>
          <div><span>SPACE</span> — Jump</div>
          <div><span>E</span> — Enter / exit building</div>
          <div><span>ESC</span> — Open Menu</div>
        </div>
        <button type="button" class="matrix-btn matrix-back" data-action="back">[ BACK ]</button>
      `;
    } else {
      const snap = this._settings.snapshot();
      const ui = snap.npcDensity;
      panel.innerHTML = `
        <div class="matrix-screen-title">SETTINGS</div>
        <div class="matrix-settings">
          <div class="matrix-setting-row">
            <label for="ui-quality">Graphics Quality</label>
            <select id="ui-quality" data-setting="quality">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div class="matrix-setting-row">
            <label for="ui-bloom">Bloom</label>
            <input id="ui-bloom" type="checkbox" data-setting="bloom" />
          </div>
          <div class="matrix-setting-row">
            <label for="ui-rain">Digital Rain</label>
            <input id="ui-rain" type="checkbox" data-setting="digitalRain" />
          </div>
          <div class="matrix-setting-row">
            <label for="ui-post">Post Processing</label>
            <input id="ui-post" type="checkbox" data-setting="postProcessing" />
          </div>
          <div class="matrix-setting-row">
            <label for="ui-npc">NPC Density</label>
            <select id="ui-npc" data-setting="npcDensity">
              <option value="50">Low</option>
              <option value="100">Medium</option>
              <option value="250">High</option>
              <option value="500">Ultra</option>
            </select>
          </div>
        </div>
        <button type="button" class="matrix-btn matrix-back" data-action="back">[ BACK ]</button>
      `;
      const qualityEl = panel.querySelector('#ui-quality');
      const bloomEl = panel.querySelector('#ui-bloom');
      const rainEl = panel.querySelector('#ui-rain');
      const postEl = panel.querySelector('#ui-post');
      const npcEl = panel.querySelector('#ui-npc');
      if (qualityEl instanceof HTMLSelectElement) {
        qualityEl.value = snap.quality;
      }
      if (bloomEl instanceof HTMLInputElement) {
        bloomEl.checked = snap.bloom;
      }
      if (rainEl instanceof HTMLInputElement) {
        rainEl.checked = snap.digitalRain;
      }
      if (postEl instanceof HTMLInputElement) {
        postEl.checked = snap.postProcessing;
      }
      if (npcEl instanceof HTMLSelectElement) {
        npcEl.value = String(ui);
      }

      panel.querySelectorAll('[data-setting]').forEach((el) => {
        el.addEventListener('change', () => {
          this._onUiInteract?.();
          this._onSettingControlChange(el);
        });
      });
    }

    panel.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const action = target.closest('[data-action]')?.getAttribute('data-action');
      if (!action) return;
      this._onUiInteract?.();
      if (action === 'enter') this._onEnter();
      if (action === 'settings') {
        this._screen = 'settings';
        this._renderScreen();
      }
      if (action === 'controls') {
        this._screen = 'controls';
        this._renderScreen();
      }
      if (action === 'back') {
        this._screen = 'main';
        this._renderScreen();
      }
    });

    this._root.appendChild(panel);
  }

  /** @param {Element} el */
  _onSettingControlChange(el) {
    const key = el.getAttribute('data-setting');
    if (!key) return;
    /** @type {Record<string, unknown>} */
    const patch = {};
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
      patch[key] = el.checked;
    } else if (el instanceof HTMLSelectElement) {
      if (key === 'npcDensity') {
        patch[key] = Number(el.value);
      } else {
        patch[key] = el.value;
      }
    }
    this._settings.applyPatch(patch);
    this._settings.syncGraphicsSettings();
    this._onSettingsChange();
  }
}
